import { useCallback, useEffect, useId, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell } from 'lucide-react';
import {
  useNotifications,
  useUnreadNotificationCount,
  useMarkNotificationRead,
  useMarkAllNotificationsRead,
  type Notification,
} from '@field-tickets/shared';
import { Avatar } from '@/components/ui/Avatar';
import { useIsMobile } from '@/hooks/useDeviceType';
import { formatNotificationTime } from './formatNotificationTime';
import { isSafeRelativeLink } from '@/utils/safeLink';

/**
 * NotificationBell — topbar bell + unread badge + dropdown panel.
 *
 * Shows up to 10 most-recent notifications. Click a row to mark it read and
 * navigate to its `link`. The footer has Mark-all-read + a "View all" link to
 * `/notifications`.
 *
 * The unread count comes from `useUnreadNotificationCount` which polls every
 * 30s while the tab is visible. Badge hides when zero, shows "99+" if > 99.
 *
 * Implementation mirrors `UserMenu` (hand-rolled popover) for visual + a11y
 * consistency: Escape closes, click-outside closes, focus returns to trigger.
 *
 * This is a non-modal popover, NOT a focus-trapping modal: the page behind it
 * stays interactive and Tab is allowed to leave the panel. So the panel is
 * exposed as `role="region"` (with `aria-haspopup="true"` on the trigger),
 * matching the actual behavior — a `role="dialog"`/`aria-modal` contract would
 * promise a focus trap this popover deliberately doesn't implement.
 *
 * On mobile (<640px) the dropdown becomes a near-full-width sheet anchored
 * near the top of the viewport rather than glued to the bell, so the panel
 * doesn't overflow off-screen.
 */
export function NotificationBell() {
  const navigate = useNavigate();
  const triggerRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const panelId = useId();
  const [open, setOpen] = useState(false);
  const isMobile = useIsMobile();

  const unreadCountQuery = useUnreadNotificationCount();
  const unreadCount = unreadCountQuery.data ?? 0;

  // Only fetch the list while the dropdown is open — keep idle cost zero.
  const listQuery = useNotifications({ limit: 10, enabled: open });
  const items = listQuery.data?.items ?? [];

  const markRead = useMarkNotificationRead();
  const markAllRead = useMarkAllNotificationsRead();

  const close = useCallback((returnFocus: boolean) => {
    setOpen(false);
    if (returnFocus) {
      requestAnimationFrame(() => triggerRef.current?.focus());
    }
  }, []);

  // Click-outside (only while open).
  useEffect(() => {
    if (!open) return;
    function onPointer(e: PointerEvent) {
      const target = e.target as Node | null;
      if (!target) return;
      if (panelRef.current?.contains(target)) return;
      if (triggerRef.current?.contains(target)) return;
      close(false);
    }
    document.addEventListener('pointerdown', onPointer);
    return () => document.removeEventListener('pointerdown', onPointer);
  }, [open, close]);

  // Escape (only while open).
  useEffect(() => {
    if (!open) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        e.preventDefault();
        close(true);
      }
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, close]);

  function handleRowClick(n: Notification) {
    if (!n.read_at) {
      markRead.mutate(n.id);
    }
    close(false);
    if (n.link && isSafeRelativeLink(n.link)) {
      navigate(n.link);
    }
  }

  function handleMarkAll() {
    markAllRead.mutate();
  }

  function handleViewAll() {
    close(false);
    navigate('/notifications');
  }

  const badgeText = unreadCount > 99 ? '99+' : String(unreadCount);
  const triggerLabel =
    unreadCount > 0
      ? `Notifications (${unreadCount} unread)`
      : 'Notifications';

  const panelClass = isMobile
    ? 'ft-notif__panel ft-notif__panel--mobile'
    : 'ft-notif__panel';

  return (
    <div className="ft-notif">
      <button
        ref={triggerRef}
        type="button"
        className="ft-notif__trigger"
        aria-haspopup="true"
        aria-expanded={open}
        aria-controls={open ? panelId : undefined}
        aria-label={triggerLabel}
        onClick={() => setOpen((v) => !v)}
      >
        <Bell size={16} aria-hidden="true" />
        {unreadCount > 0 ? (
          <span className="ft-notif__badge" aria-hidden="true">
            {badgeText}
          </span>
        ) : null}
      </button>

      {open ? (
        <>
          {isMobile ? (
            <div className="ft-notif__backdrop" aria-hidden="true" />
          ) : null}
          <div
            ref={panelRef}
            id={panelId}
            role="region"
            aria-label="Notifications"
            className={panelClass}
          >
            <div className="ft-notif__header">
              <span className="ft-notif__title">Notifications</span>
              {unreadCount > 0 ? (
                <button
                  type="button"
                  className="ft-notif__link-btn"
                  onClick={handleMarkAll}
                  disabled={markAllRead.isPending}
                >
                  Mark all read
                </button>
              ) : null}
            </div>

            <div className="ft-notif__list">
              {listQuery.isLoading ? (
                <div className="ft-notif__empty">Loading…</div>
              ) : items.length === 0 ? (
                <div className="ft-notif__empty">You&rsquo;re all caught up.</div>
              ) : (
                items.map((n) => (
                  <NotificationRow
                    key={n.id}
                    notification={n}
                    onSelect={handleRowClick}
                  />
                ))
              )}
            </div>

            <div className="ft-notif__footer">
              <button
                type="button"
                className="ft-notif__link-btn"
                onClick={handleViewAll}
              >
                View all
              </button>
            </div>
          </div>
        </>
      ) : null}
    </div>
  );
}

function NotificationRow({
  notification,
  onSelect,
}: {
  notification: Notification;
  onSelect: (n: Notification) => void;
}) {
  const isUnread = !notification.read_at;
  // We don't have actor name on the notification row — only `actor_user_id`.
  // Fall back to deriving an avatar/initials from the title's first word so
  // the row still feels populated. This keeps v1 self-contained; v1.1 can add
  // a JOIN to surface the actor's display name.
  const avatarSeed = notification.title || 'System';
  return (
    <button
      type="button"
      className={
        isUnread ? 'ft-notif__row ft-notif__row--unread' : 'ft-notif__row'
      }
      onClick={() => onSelect(notification)}
    >
      <span className="ft-notif__row-avatar" aria-hidden="true">
        <Avatar name={avatarSeed} size={32} />
      </span>
      <span className="ft-notif__row-body">
        <span className="ft-notif__row-title">{notification.title}</span>
        {notification.body ? (
          <span className="ft-notif__row-text">{notification.body}</span>
        ) : null}
        <span className="ft-notif__row-time">
          {formatNotificationTime(notification.created_at)}
        </span>
      </span>
    </button>
  );
}
